/**
 * Login do widget por POPUP no domínio do Tasks (DEV-109).
 *
 * O widget não embarca o Firebase Auth (custaria ~4x o bundle): abre uma janela na nossa
 * origem, a pessoa entra pelo login que já existe lá, e a página devolve uma **sessão
 * opaca** por `postMessage`. Com ela o widget passa a listar todos os chamados daquele
 * e-mail, não só os abertos neste browser.
 *
 * Duas checagens no retorno, e nenhuma é opcional:
 *
 *  1. **`event.origin` tem de ser a nossa origem.** Qualquer página pode mandar
 *     `postMessage` para esta janela; sem conferir a origem, um script do próprio site
 *     anfitrião injetaria uma "sessão" à escolha dele.
 *  2. **o payload é validado campo a campo** antes de ir para o storage.
 *
 * (Do outro lado o cuidado é simétrico: a página só faz `postMessage` para a origem que a
 * allowlist do relator aprovou, nunca para a que veio na URL.)
 */

export interface SessaoWidget {
  sessao: string;
  /** ISO — o widget não valida, só exibe/expira; a autoridade é o servidor. */
  expiraEm?: string;
  nome?: string | null;
  /**
   * O e-mail da conta que entrou. Só exibição — a autoridade é a sessão, e é o SERVIDOR
   * que carimba o e-mail no chamado (o widget não manda identidade quando o relator exige
   * login). Serve para o formulário dizer em nome de quem o chamado vai, que é o recibo do
   * login numa tela de um site que não é o nosso.
   */
  email?: string | null;
  /**
   * O endereço que configura ESTE relator no Tasks (DEV-223) — presente só quando quem
   * entrou é **admin** da organização dona dele. É o servidor que decide: o widget não
   * conhece papéis nem o slug da org, e não teria como conferir nenhum dos dois.
   *
   * Ausente = o menu não mostra o item, e é o caso de todo mundo que não é admin.
   */
  configUrl?: string;
}

/**
 * A `configUrl`, aceita só se for um endereço http(s) de verdade.
 *
 * Dois caminhos chegam aqui e nenhum é confiável por construção: o `postMessage` do
 * popup (validado pela origem, mas o formato ainda é campo a campo) e o `localStorage`,
 * que é **do site anfitrião** — ele pode reescrever a chave do widget à vontade. Sem
 * esta peneira, um valor `javascript:…` guardado ali viraria o destino de um
 * `window.open` disparado por um item do NOSSO menu.
 */
export function urlDeConfig(bruta: unknown): string | undefined {
  if (typeof bruta !== "string" || bruta === "") return undefined;
  try {
    const u = new URL(bruta);

    return u.protocol === "http:" || u.protocol === "https:" ? bruta : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A conta que ENTROU é a que o host disse que está na página? (DEV-316)
 *
 * O host declara no init quem está ali (`reporter.email`), e com `exigeLogin` (DEV-260) o
 * chamado sai no e-mail da SESSÃO. Sem comparar os dois, quem tem o Google logado noutra
 * conta abre o chamado no nome errado — e nada na tela diz isso, porque cada lado sozinho
 * está certo.
 *
 * **Não é gate de segurança, e não pode ser tratado como um.** O `reporter.email` é
 * alegação de uma página de terceiro; quem carimba a identidade do chamado continua sendo a
 * sessão, no servidor (é o ponto do DEV-260). Isto aqui é o aviso de conta trocada — o caso
 * real é o Google com duas contas, não a fraude.
 *
 * FAIL-OPEN nos dois lados desconhecidos, de propósito: host que não declarou nada não tem
 * o que conferir, e sessão sem e-mail (provedor que não devolve, ou sessão guardada por um
 * bundle anterior ao campo) não pode trancar quem já entrou.
 */
export function contaConfere(esperado: string, daSessao: string | null | undefined): boolean {
  const host = esperado.trim().toLowerCase();
  const conta = (daSessao ?? "").trim().toLowerCase();
  if (host === "" || conta === "") return true;

  return host === conta;
}

/** Caminho da página de login, no front. Contrato com `web/lib/data/rotas.ts`. */
const ROTA_AUTH = "/widget/auth";

/**
 * A origem do Tasks e a URL do popup, derivadas do endpoint do intake.
 *
 * O intake e o front costumam estar na MESMA origem (o Laravel serve o bundle ao lado de
 * `/intake/v1/bugs`), que é o caso do snippet padrão. Quando não estão — bundle num CDN,
 * ou front em outro host —, quem sabe disso é o servidor: a `portalUrl` do 202 aponta o
 * front de verdade. Por isso `portalUrl` tem precedência quando o widget a conhece.
 */
export function urlDoPopup(
  endpoint: string,
  portalUrl: string | undefined,
  widgetToken: string,
  trocarConta = false,
): string {
  const base = origemDoFront(endpoint, portalUrl);
  if (!base) return "";
  const u = new URL(base + ROTA_AUTH);
  u.searchParams.set("relator", widgetToken);
  u.searchParams.set("origem", window.location.origin);
  // "Não reaproveite a conta que já está logada aí" (DEV-316). Sem isto o popup emite a
  // sessão de quem já entrou ANTES de mostrar tela nenhuma, e quem errou a conta na
  // primeira vez nunca alcança o seletor — clicar "Entrar" de novo devolve a mesma
  // conta recusada, para sempre. É uma BANDEIRA, e não o e-mail esperado: o popup não
  // precisa saber quem é a pessoa certa (quem compara é o widget), e mandar o endereço
  // aqui o poria numa query string, em log de acesso e no histórico do browser.
  if (trocarConta) u.searchParams.set("trocar", "1");

  return u.href;
}

/** A origem esperada do `postMessage` de volta (a mesma do popup). */
export function origemDoFront(endpoint: string, portalUrl?: string): string {
  for (const candidata of [portalUrl, endpoint]) {
    if (typeof candidata !== "string" || candidata === "") continue;
    try {
      const u = new URL(candidata);
      if (u.protocol === "http:" || u.protocol === "https:") return u.origin;
    } catch {
      /* candidata inválida — tenta a próxima */
    }
  }

  return "";
}

/**
 * Valida uma mensagem recebida. Devolve a sessão ou null — e é aqui que a origem é
 * conferida, porque de nada serve validar o formato de um payload de quem não devia estar
 * falando com a gente.
 */
export function sessaoDaMensagem(evento: MessageEvent, origemEsperada: string): SessaoWidget | null {
  if (!origemEsperada || evento.origin !== origemEsperada) return null;
  const d = evento.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object" || d.tipo !== "altbug:sessao") return null;
  if (typeof d.sessao !== "string" || d.sessao === "") return null;

  return {
    sessao: d.sessao,
    expiraEm: typeof d.expiraEm === "string" ? d.expiraEm : undefined,
    nome: typeof d.nome === "string" && d.nome !== "" ? d.nome : null,
    email: typeof d.email === "string" && d.email !== "" ? d.email : null,
    configUrl: urlDeConfig(d.configUrl),
  };
}

/**
 * Por que não é `SessaoWidget | null`: os três desfechos sem sessão pedem respostas
 * diferentes da UI, e um `null` só os empilha.
 *
 * - `bloqueado` — o browser recusou a janela. É acionável ("libere popups deste site") e
 *   a pessoa não viu tela nenhuma: sem aviso, o clique parece não ter feito nada.
 * - `indisponivel` — não há origem do Tasks para abrir (nem `portalUrl`, nem endpoint
 *   utilizável). É erro de configuração, não da pessoa.
 * - `cancelado` — fechou a janela. Inclui quem viu a recusa do servidor lá dentro (o
 *   403 "Não há chamados desta conta neste site.") e desistiu: a mensagem é do popup,
 *   que é quem sabe o motivo, então aqui não repetimos nada.
 */
export type ResultadoEntrar =
  | { ok: true; sessao: SessaoWidget }
  | { ok: false; motivo: "bloqueado" | "indisponivel" | "cancelado" };

/**
 * Abre o popup e resolve quando a sessão chega — ou com o motivo de não ter chegado.
 *
 * O `window.open` tem de acontecer no MESMO tick do clique, senão o bloqueador de popup
 * do browser recusa — daí a função ser chamada direto do handler, sem `await` antes.
 */
export function entrarPorPopup(
  endpoint: string,
  portalUrl: string | undefined,
  widgetToken: string,
  trocarConta = false,
): Promise<ResultadoEntrar> {
  const url = urlDoPopup(endpoint, portalUrl, widgetToken, trocarConta);
  const origemEsperada = origemDoFront(endpoint, portalUrl);
  if (!url || !origemEsperada) return Promise.resolve({ ok: false, motivo: "indisponivel" });

  const janela = window.open(url, "altbug-auth", "width=460,height=680,noopener=no");
  if (!janela) return Promise.resolve({ ok: false, motivo: "bloqueado" });

  return new Promise((resolve) => {
    let pronto = false;

    const finalizar = (r: ResultadoEntrar): void => {
      if (pronto) return;
      pronto = true;
      window.removeEventListener("message", onMessage);
      clearInterval(vigia);
      resolve(r);
    };

    const onMessage = (e: MessageEvent): void => {
      const s = sessaoDaMensagem(e, origemEsperada);
      if (s) finalizar({ ok: true, sessao: s });
    };
    window.addEventListener("message", onMessage);

    // Fechou sem entrar: sem isto a Promise ficaria pendurada para sempre e o botão do
    // widget continuaria em "Entrando…".
    const vigia = setInterval(() => {
      if (janela.closed) finalizar({ ok: false, motivo: "cancelado" });
    }, 500);
  });
}
